import { useState, ComponentProps, useMemo, useEffect } from 'react';
import moment from 'moment';
import { SingleValue } from 'react-select';
import { useRouter } from 'next/router';
import GuestCounter from '../GuestCounter/GuestCounter';
import DatePicker from '../DatePicker/DatePicker';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import { DEFAULT_CHECK_IN_DATE_MOMENT, DEFAULT_GUEST_COUNT } from '../../../core/constants';
import getGuestCount from '../../../core/booking/getGuestCount';
import HotelSearchSelectOption from '../../../../types/HotelSearchSelectOption';
import { PREFECTURES } from '../../../core/japaneseRegions';
import bookingSlice from '../../../core/booking/slice';
import { useSelector } from '../../../core/redux/store';

export type RoomAndGuestCount = ComponentProps<typeof GuestCounter>['roomAndGuestCount'];
export type CheckInAndOutDates = ComponentProps<typeof DatePicker>['checkInAndOutDates'];

const useHotelSearchConditions = (term?: SingleValue<HotelSearchSelectOption>) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const bookingState = useSelector((state) => state.booking);
  const getStoredGuestCount = getGuestCount(
    (router?.query?.guestPerRoom as string) || bookingState.guestCountString || DEFAULT_GUEST_COUNT
  );

  const [searchTerm, setSearchTerm] = useState<SingleValue<HotelSearchSelectOption>>();
  const [roomAndGuestCount, setRoomAndGuestCount] = useState<RoomAndGuestCount>({
    roomCount: 1,
    ...getGuestCount(DEFAULT_GUEST_COUNT),
  });

  const calculatedCheckInDate = useMemo(() => {
    if (
      router?.query?.checkInDate &&
      moment(router?.query?.checkInDate).isSameOrAfter(moment(), 'days')
    ) {
      return moment(router?.query?.checkInDate);
    }
    if (
      bookingState.checkInDateISOString &&
      moment(bookingState.checkInDateISOString).isSameOrAfter(moment(), 'days')
    ) {
      return moment(bookingState.checkInDateISOString);
    }
    return DEFAULT_CHECK_IN_DATE_MOMENT;
  }, [bookingState.checkInDateISOString, router?.query?.checkInDate]);

  const calculatedCheckOutDate = useMemo(() => {
    if (
      router?.query?.checkOutDate &&
      moment(router?.query?.checkOutDate).isAfter(calculatedCheckInDate, 'days')
    ) {
      return moment(router?.query?.checkOutDate);
    }
    if (
      bookingState.checkOutDateISOString &&
      moment(bookingState.checkOutDateISOString).isAfter(calculatedCheckInDate, 'days')
    ) {
      return moment(bookingState.checkOutDateISOString);
    }
    return moment(calculatedCheckInDate).add(1, 'days');
  }, [bookingState.checkOutDateISOString, calculatedCheckInDate, router?.query?.checkOutDate]);

  const [checkInAndOutDates, setCheckInAndOutDates] = useState<CheckInAndOutDates>({
    checkInDate: null,
    checkOutDate: null,
  });

  useEffect(() => {
    // set initial value
    setSearchTerm(term || bookingState.searchTerm || null);
    setCheckInAndOutDates({
      checkInDate: calculatedCheckInDate,
      checkOutDate: calculatedCheckOutDate,
    });
    setRoomAndGuestCount({
      roomCount: router.query?.roomCount ? Number(router.query?.roomCount) : bookingState.roomCount,
      adultCount: getStoredGuestCount.adultCount,
      seniorElementarySchoolChildCount: getStoredGuestCount.seniorElementarySchoolChildCount,
      juniorElementarySchoolChildCount: getStoredGuestCount.juniorElementarySchoolChildCount,
      infantWithMealAndFutonCount: getStoredGuestCount.infantWithMealAndFutonCount,
      infantWithMealCount: getStoredGuestCount.infantWithMealCount,
      infantWithFutonCount: getStoredGuestCount.infantWithFutonCount,
      infantWithoutMealAndFutonCount: getStoredGuestCount.infantWithoutMealAndFutonCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { roomCount, ...guestCounts } = roomAndGuestCount;
  const guestCountString = Object.values(guestCounts).join('_');

  useEffect(() => {
    const selectedPrefecture = PREFECTURES.find(
      (prefecture) => prefecture.path === searchTerm?.value
    );

    let type: 'prefecture' | 'property' | 'region' | 'city' = 'region';

    if (selectedPrefecture) {
      type = 'prefecture';
    } else if (searchTerm?.type === 'property') {
      type = 'property';
    } else if (searchTerm?.type === 'city') {
      type = 'city';
    }

    dispatch(
      bookingSlice.actions.updateSearchCondition({
        type,
        searchTerm: type !== 'region' && searchTerm?.value ? searchTerm : null,
        guestCountString,
        checkInDateISOString: checkInAndOutDates?.checkInDate?.toISOString() || '',
        checkOutDateISOString: checkInAndOutDates?.checkOutDate?.toISOString() || '',
        roomCount,
      })
    );
  }, [
    checkInAndOutDates?.checkInDate,
    checkInAndOutDates?.checkOutDate,
    dispatch,
    guestCountString,
    roomCount,
    searchTerm,
  ]);

  return {
    dispatch,
    roomAndGuestCount,
    setRoomAndGuestCount,
    checkInAndOutDates,
    setCheckInAndOutDates,
    searchTerm,
    setSearchTerm,
    guestCountString,
    roomCount,
  };
};

export default useHotelSearchConditions;
